import type { Labelled } from "./common.js";

/**
 * الإشعارات.
 *
 * كان النظام كله سحبًا: تُحسب الإشارات وتنتظر من يفتح الشاشة. هذه الوحدة
 * تقلبه دفعًا — يصل الخبر إلى صاحبه في وقته.
 */

export const NOTIFICATION_PRIORITIES = [
  "LOW",
  "NORMAL",
  "HIGH",
  "URGENT",
] as const;
export type NotificationPriority = (typeof NOTIFICATION_PRIORITIES)[number];

export const NOTIFICATION_PRIORITY_DEFS: readonly Labelled<NotificationPriority>[] =
  [
    { key: "LOW", label: "منخفض", kind: "neutral" },
    { key: "NORMAL", label: "عادي", kind: "info" },
    { key: "HIGH", label: "مهم", kind: "warning" },
    { key: "URGENT", label: "عاجل", kind: "danger" },
  ] as const;

export const NOTIFICATION_PRIORITY_MAP = Object.fromEntries(
  NOTIFICATION_PRIORITY_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<NotificationPriority, Labelled<NotificationPriority>>>;

/* ==========================================================================
   أنواع الإشعارات
   ========================================================================== */

export const NOTIFICATION_KINDS = [
  "MEETING_REMINDER",
  "CALL_REMINDER",
  "EVENT_CANCELLED",
  "TASK_DUE",
  "ASSIGNED",
  "LEAVE_REQUEST",
  "LEAVE_DECISION",
  "REQUEST_SUBMITTED",
  "REQUEST_DECISION",
  "INVOICE_OVERDUE",
  "INVOICE_ISSUED",
  "PAYMENT_RECEIVED",
  "QUOTATION_EXPIRING",
  "DEAL_STALLED",
  "LEAD_QUALIFIED",
  "LEAD_UNTOUCHED",
  "PAYROLL_READY",
  "DEPRECIATION_DUE",
  "TEAM_CHANGED",
  "WAR_ROOM_SESSION",
  "FEEDBACK_MENTION",
  "CHAT_MENTION",
  "JOY_TASK",
  "JOY_ALERT",
  "SUBSCRIPTION",
  "SYSTEM",
] as const;

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface NotificationKindDef {
  key: NotificationKind;
  label: string;
  description: string;
  /** المجموعة في شاشة التفضيلات. */
  group: string;
  defaultPriority: NotificationPriority;
  /**
   * ينبثق افتراضيًا.
   *
   * يُحجز للما لا يحتمل الانتظار: موعد بعد دقائق، أو حدث يفوت إن لم يُرَ
   * الآن. جعل كل شيء منبثقًا يُدرّب المستخدم على إغلاقه بلا قراءة، فيضيع
   * المهم مع الكثير.
   */
  defaultPopup: boolean;
  /** لا يُطفأ: يخصّ المال أو الصلاحيات. */
  mandatory?: boolean;
}

export const NOTIFICATION_KIND_DEFS: readonly NotificationKindDef[] = [
  {
    key: "MEETING_REMINDER",
    label: "تذكير باجتماع",
    description: "قبل موعد الاجتماع بالمدة التي تحددها.",
    group: "المواعيد",
    defaultPriority: "HIGH",
    defaultPopup: true,
  },
  {
    key: "CALL_REMINDER",
    label: "تذكير بمكالمة",
    description: "قبل موعد المكالمة بالمدة التي تحددها.",
    group: "المواعيد",
    defaultPriority: "HIGH",
    defaultPopup: true,
  },
  {
    key: "EVENT_CANCELLED",
    label: "إلغاء موعد",
    description: "حين يُلغى موعد أنت من حضوره.",
    group: "المواعيد",
    defaultPriority: "HIGH",
    defaultPopup: true,
  },
  {
    key: "TASK_DUE",
    label: "مهمة تستحق اليوم",
    description: "مهمة مسندة إليك بلغ موعد استحقاقها.",
    group: "المواعيد",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "ASSIGNED",
    label: "إسناد إليك",
    description: "عميل أو صفقة أو مهمة أُسندت إليك.",
    group: "العمل اليومي",
    defaultPriority: "NORMAL",
    defaultPopup: true,
  },
  {
    key: "LEAD_UNTOUCHED",
    label: "عميل محتمل بلا تواصل",
    description: "عميل مسند إليك لم يُتواصل معه.",
    group: "العمل اليومي",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "LEAD_QUALIFIED",
    label: "عميل محتمل صار مؤهَّلًا",
    description: "قُبل أوّلُ عرضٍ له — حان تحويله إلى عميل لتكتمل الدورة.",
    group: "العمل اليومي",
    defaultPriority: "HIGH",
    /* منبثقٌ ابتداءً: تحويلٌ يُؤجَّل يُفوّت دورةً بدأت. */
    defaultPopup: true,
  },
  {
    key: "DEAL_STALLED",
    label: "صفقة راكدة",
    description: "صفقة لم تتحرك مرحلتها منذ مدة.",
    group: "العمل اليومي",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "QUOTATION_EXPIRING",
    label: "عرض سعر يوشك أن ينتهي",
    description: "عرض أُرسل ولم يُبتّ فيه وقاربت صلاحيته.",
    group: "العمل اليومي",
    defaultPriority: "HIGH",
    defaultPopup: false,
  },
  {
    key: "INVOICE_OVERDUE",
    label: "فاتورة متأخرة",
    description: "فاتورة تجاوزت موعد استحقاقها.",
    group: "المال",
    defaultPriority: "HIGH",
    defaultPopup: false,
    mandatory: true,
  },
  {
    key: "INVOICE_ISSUED",
    label: "فاتورة صدرت تلقائيًا",
    description: "اشتراك دوري أصدر فاتورته.",
    group: "المال",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "PAYMENT_RECEIVED",
    label: "تحصيل",
    description: "سُجِّل تحصيل على فاتورة تخصّك.",
    group: "المال",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "PAYROLL_READY",
    label: "دورة مرتب تنتظر الاعتماد",
    description: "دورة مرتب مسودة لم تُعتمد بعد.",
    group: "المال",
    defaultPriority: "HIGH",
    defaultPopup: false,
    mandatory: true,
  },
  {
    key: "DEPRECIATION_DUE",
    label: "إهلاك شهري مستحق",
    description: "شهر مضى ولم يُرحَّل إهلاكه.",
    group: "المال",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "LEAVE_REQUEST",
    label: "طلب إجازة ينتظر قرارك",
    description: "طلب إجازة قيد الاعتماد.",
    group: "الموارد البشرية",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "LEAVE_DECISION",
    label: "قرار في طلب إجازتك",
    description: "اعتُمد طلبك أو رُفض.",
    group: "الموارد البشرية",
    defaultPriority: "HIGH",
    defaultPopup: true,
  },
  /*
   * الطلباتُ غيرُ الإجازات.
   *
   * إذنٌ ومصروفٌ وخطابٌ لا تمسّ رصيدًا ولا تدخل حسابَ المرتّب، ومن أراد
   * أن يُطفئ إشعارَ الإجازات لا يريد بالضرورة أن يُطفئ إشعارَ المصروفات
   * التي ينتظر ردَّها.
   */
  {
    key: "REQUEST_SUBMITTED",
    label: "طلب موظّف ينتظر قرارك",
    description: "إذن أو مصروف أو خطاب قيد الاعتماد.",
    group: "الموارد البشرية",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "REQUEST_DECISION",
    label: "قرار في طلبك",
    description: "وُوفق على طلبك أو رُفض.",
    group: "الموارد البشرية",
    defaultPriority: "NORMAL",
    defaultPopup: false,
  },
  {
    key: "TEAM_CHANGED",
    label: "تغيير في حسابك",
    description: "تغيّر دورك أو صلاحياتك.",
    group: "الحساب",
    defaultPriority: "HIGH",
    defaultPopup: true,
    mandatory: true,
  },
  {
    /*
     * ينبثق ولا يُطفأ.
     *
     * الجلسة لها وقت يبدأ وينتهي، وإشعارٌ يُقرأ بعد ساعة من بدئها لا معنى
     * له — فات ما دُعي إليه. وهي دعوة من مدير إلى فريقه لا تنبيهًا عامًّا،
     * فإطفاؤها يجعل من أطفأها غائبًا عن عمل يُنتظر منه.
     */
    key: "WAR_ROOM_SESSION",
    label: "دعوة إلى غرفة العمليات",
    description: "حين تبدأ جلسة تركيز أنت من المدعوّين إليها.",
    group: "المهام",
    defaultPriority: "URGENT",
    defaultPopup: true,
    mandatory: true,
  },
  {
    /*
     * الذكر يُشعِر ولا يُطفأ.
     *
     * من يُذكر باسمه في ملاحظة قد سُئل صراحةً — «@فلان راجع هذا» — وإشعارٌ
     * يُطفأ يجعل السائل ينتظر جوابًا لا يعلم المسؤول أنه مطلوب منه.
     *
     * ولا ينبثق: الملاحظة تُقرأ حين يتفرّغ لها، والانبثاق يُحجز لما يفوت.
     */
    key: "FEEDBACK_MENTION",
    label: "ذُكرت في ملاحظة",
    description: "حين يذكرك زميل باسمك في تغذية راجعة على سجل.",
    group: "العمل اليومي",
    defaultPriority: "HIGH",
    defaultPopup: false,
    mandatory: true,
  },
  {
    /*
     * الذكر في الشات يُشعِر ولا ينبثق.
     *
     * من ذُكر باسمه في قناة قد سُئل صراحةً، وقناةٌ لا يفتحها طوال اليوم
     * تعني سؤالًا ينتظر جوابًا لا يعلم صاحبه أنه مطلوب منه.
     *
     * ولا يُطفأ لأن الذكر اختيارُ من كتبه لا تنبيهًا عامًّا. ولا ينبثق
     * لأن الشات يُقرأ حين يتفرّغ له — والانبثاق يُحجز لما يفوت.
     */
    key: "CHAT_MENTION",
    label: "ذُكرت في الشات",
    description: "حين يذكرك زميل باسمك في قناة أو رسالة مباشرة.",
    group: "العمل اليومي",
    defaultPriority: "HIGH",
    defaultPopup: false,
    mandatory: true,
  },
  {
    /*
     * نتيجةُ مهمّةٍ طلبها المدير من جوي بنفسه — لا تُفرض عليه ولا تنبثق:
     * التقريرُ الصباحيّ يُقرأ حين يُفتح لا يقاطع ما بين يديه.
     */
    key: "JOY_TASK",
    label: "مهامّ جوي",
    description: "نتيجةُ مهمّةٍ جدولتَها مع جوي في موعدها.",
    group: "العمل اليومي",
    defaultPriority: "NORMAL",
    defaultPopup: false,
    mandatory: false,
  },
  {
    /* خللٌ وجدته قاعدةُ مراقبة — التنبيهُ غرضُه أن يُرى الآن. */
    key: "JOY_ALERT",
    label: "تنبيهات جوي",
    description: "حين تجد قاعدةُ مراقبةٍ وضعتَها مع جوي خللًا في العمل.",
    group: "العمل اليومي",
    defaultPriority: "HIGH",
    defaultPopup: true,
    mandatory: false,
  },
  {
    key: "SUBSCRIPTION",
    label: "اشتراك الشركة",
    description: "قرب انتهاء الاشتراك أو تغيّر حالته.",
    group: "الحساب",
    defaultPriority: "URGENT",
    defaultPopup: true,
    mandatory: true,
  },
  {
    key: "SYSTEM",
    label: "رسائل النظام",
    description: "إشعارات عامة من النظام.",
    group: "الحساب",
    defaultPriority: "NORMAL",
    defaultPopup: false,
    mandatory: true,
  },
] as const;

export const NOTIFICATION_KIND_MAP = Object.fromEntries(
  NOTIFICATION_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<NotificationKind, NotificationKindDef>>;

/** المجموعات بترتيب العرض. */
export const NOTIFICATION_GROUPS = [
  ...new Set(NOTIFICATION_KIND_DEFS.map((d) => d.group)),
] as readonly string[];

/* ==========================================================================
   التفضيلات
   ========================================================================== */

export interface NotificationPreference {
  enabled: boolean;
  popup: boolean;
}

export type NotificationPreferences = Partial<
  Record<NotificationKind, NotificationPreference>
>;

/**
 * مُهَل التذكير المتاحة.
 *
 * قائمة مغلقة لا حقل حر: المجدول يفحص المواعيد ضمن أقصى مهلة، ومهلة
 * اعتباطية كبيرة تجعله يمسح تقويم شهر في كل دقيقة.
 */
export const REMINDER_LEAD_OPTIONS = [5, 10, 15, 30, 60, 120, 1440] as const;
export type ReminderLead = (typeof REMINDER_LEAD_OPTIONS)[number];

export const MAX_REMINDER_LEAD = 1440;

export function reminderLabel(minutes: number): string {
  if (minutes >= 1440) return "قبل يوم";
  if (minutes >= 60) {
    const h = Math.round(minutes / 60);
    return h === 1 ? "قبل ساعة" : h === 2 ? "قبل ساعتين" : `قبل ${h} ساعات`;
  }
  return `قبل ${minutes} دقيقة`;
}

/** التفضيل الفعلي لنوع: ما اختاره المستخدم، أو الافتراضي. */
export function preferenceFor(
  kind: NotificationKind,
  prefs: NotificationPreferences | null | undefined,
): NotificationPreference {
  const def = NOTIFICATION_KIND_MAP[kind];
  const saved = prefs?.[kind];

  return {
    // الإلزامي لا يُطفأ مهما حُفظ في التفضيلات.
    enabled: def.mandatory ? true : (saved?.enabled ?? true),
    popup: saved?.popup ?? def.defaultPopup,
  };
}

/** التفضيلات كاملةً للعرض في الشاشة. */
export function allPreferences(
  prefs: NotificationPreferences | null | undefined,
): Array<NotificationKindDef & NotificationPreference> {
  return NOTIFICATION_KIND_DEFS.map((def) => ({
    ...def,
    ...preferenceFor(def.key, prefs),
  }));
}
